<template>
	<AppInlinePopover
		v-if="canSelectPrinter"
		:label="label"
		:value="selectedPrinterName"
		:icon="icon"
		:variant="variant"
		:label-class="labelClass"
		:value-class="valueClass"
		:outlined="outlined && !readonly"
		:disabled="readonly"
	>
		<template #default="{ hide }">
			<div class="w-72 py-2">
				<p class="px-3 pb-2 text-xs text-gray-500">Printer selecteren</p>
				<ul class="max-h-64 overflow-auto">
					<li v-if="printers.length == 0" class="px-3 py-2 text-sm text-gray-500">
						Geen printers beschikbaar
					</li>
					<li v-for="printer in printers" :key="printer.id">
						<button
							type="button"
							class="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-60"
							:disabled="saving"
							@click="selectPrinter(printer.id, hide)"
						>
							<AppIcon
								:icon="saving ? 'fa-spinner' : 'fa-check'"
								:spin="saving"
								fixed-width
								:class="printer.id == preferredPrinterId ? 'text-primary-500' : 'opacity-0'"
							/>
							<span class="grow truncate">{{ printer.name }}</span>
							<span v-if="printer.id == preferredPrinterId" class="text-xs text-gray-400">voorkeur</span>
						</button>
					</li>
				</ul>
			</div>
		</template>
	</AppInlinePopover>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { getPrinters, setPrinterPreference, type Printer, type ReportDesignCategoryEnum } from "@q-food/q-food";
import { useUserStore } from "~stores/User.ts";

const props = withDefaults(
	defineProps<{
		/** The print category this selector sets. Omit for the catch-all default printer (no category). */
		category?: ReportDesignCategoryEnum;
		label?: string;
		/** Optional leading icon (FontAwesome name); omit for no icon. */
		icon?: string;
		/** "text" (default) reads as a clickable name; "button" renders a toolbar chip. */
		variant?: "text" | "button";
		/** Style hooks forwarded to AppInlinePopover so the control can match its surrounding text. */
		labelClass?: string;
		valueClass?: string;
		permission?: string;
		/** Render as a bordered dropdown-input box (forwarded to AppInlinePopover). */
		outlined?: boolean;
		/** Show the category's default printer as a non-editable display (no dropdown). */
		readonly?: boolean;
	}>(),
	{ permission: "read printer" },
);

const { error, success } = useToast();
const { getPrinterId, refreshUserInfo, isAllowed } = useUserStore();

const printers = ref<Printer[]>([]);
const saving = ref(false);

const canReadPrinters = computed(() => isAllowed("read printer"));
// Whether the control renders — driven by the [permission] prop (defaults to `read printer`).
const canSelectPrinter = computed(() => isAllowed(props.permission));
const preferredPrinterId = computed(() => getPrinterId(props.category ?? null));
const selectedPrinterName = computed(
	() => printers.value.find((printer) => printer.id === preferredPrinterId.value)?.name,
);

onMounted(async () => {
	if (!canReadPrinters.value) return;
	try {
		printers.value = (await getPrinters(undefined, 0, 100, ["name"])).content ?? [];
	} catch (e) {
		console.log(e);
	}
});

async function selectPrinter(printerId: string, hide: () => void) {
	if (saving.value || printerId == preferredPrinterId.value) {
		hide();
		return;
	}
	saving.value = true;
	try {
		await setPrinterPreference({ category: props.category, printerId });
		// So getPrinterId() (and every other screen) reflects the new default immediately.
		await refreshUserInfo();
		success("Standaardprinter opgeslagen");
		hide();
	} catch (e: any) {
		error(e.message ?? "Er is iets misgegaan bij het opslaan van de standaardprinter");
	} finally {
		saving.value = false;
	}
}
</script>
